/**
 * @fileoverview Inline block for displaying occurrence repair status and actions.
 * @module inspection-renderer/violations
 *
 * Responsibilities:
 * - Display occurrence repair status (open/resolved/closed)
 * - Show repair details when resolved (fixedAt, fixedBy, fixedNotes)
 * - Provide "Mark Repaired" action button for open occurrences
 * - Provide "Undo Repair" action for resolved occurrences
 *
 * Dependencies:
 * - LoggedViolationOccurrence from ./types
 * - OccurrenceRepairModal for repair confirmation
 * - Lucide icons
 *
 * Architecture:
 * - Reusable component for embedding in any violation list/detail view
 * - Parent component handles persistence to Dexie
 * - No persistence logic — all write-back is caller responsibility
 *
 * Notes:
 * - Designed to be compact for inline use in grids/lists
 * - Can be expanded for detail views
 * - THE single shared block used by ViolationCaptureBlock, ViolationsModulePreview, CaseTimeline
 */

import { useState } from 'react';
import { CheckCircle2, Clock, XCircle, Wrench, Undo2 } from 'lucide-react';
import type { LoggedViolationOccurrence } from './types';
import { OccurrenceRepairModal, type OccurrenceRepairData } from './OccurrenceRepairModal';

/**
 * @contract OccurrenceRepairBlockProps
 *
 * Purpose:
 * - Props for the inline repair status/action block
 *
 * Fields:
 * - occurrence: The occurrence to display/act on
 * - label: Optional display label for the occurrence
 * - readOnly: Whether actions are disabled (default: false)
 * - currentUserId: User ID to record as fixedBy
 * - userOptions: Optional list of users for dropdown (future)
 * - onMarkRepaired: Called when user confirms repair
 * - onReopen: Called when user undoes a repair
 */
export interface OccurrenceRepairBlockProps {
  /** The occurrence to display/act on */
  occurrence: LoggedViolationOccurrence;
  /** Optional display label for the occurrence */
  label?: string;
  /** Violation code for modal context */
  violationCode?: string;
  /** Violation description for modal context */
  violationDescription?: string;
  /** Whether actions are disabled (default: false) */
  readOnly?: boolean;
  /** Current user ID for recording fixedBy */
  currentUserId?: string;
  /** Optional list of users for dropdown (future) */
  userOptions?: Array<{ id: string; name: string }>;
  /** Called when user confirms repair with data */
  onMarkRepaired: (occurrenceId: string, data: OccurrenceRepairData) => void | Promise<void>;
  /** Called when user undoes a repair */
  onReopen?: (occurrenceId: string) => void | Promise<void>;
  /** Whether to use compact layout (default: true) */
  compact?: boolean;
}

/**
 * Format a date string for display.
 * Returns "Today", "Yesterday", or formatted date.
 */
function formatRepairDate(isoString: string | undefined): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * @component OccurrenceRepairBlock
 *
 * Purpose:
 * - Inline UI for occurrence repair status and actions
 * - THE single shared block for all violation surfaces
 *
 * Props:
 * - occurrence: LoggedViolationOccurrence to display
 * - label: Optional label for the occurrence
 * - violationCode: Code string for modal context
 * - violationDescription: Optional description for modal
 * - readOnly: Disable action buttons
 * - currentUserId: User ID for fixedBy
 * - onMarkRepaired: Callback when repair is confirmed
 * - onReopen: Optional callback to undo repair
 * - compact: Use compact layout (default true)
 *
 * Behavior:
 * - Shows status badge (open/resolved/closed)
 * - For open: shows "Mark Repaired" button → opens modal
 * - For resolved: shows repair details + optional undo button
 * - For closed: shows closed status (no actions)
 *
 * Data Source:
 * - Props only (presentational)
 *
 * Constraints:
 * - No direct API calls — parent handles persistence
 */
export function OccurrenceRepairBlock({
  occurrence,
  label,
  violationCode,
  violationDescription,
  readOnly = false,
  onMarkRepaired,
  onReopen,
  compact = true,
}: OccurrenceRepairBlockProps) {
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirmRepair = async (data: OccurrenceRepairData) => {
    setIsSubmitting(true);
    try {
      await onMarkRepaired(occurrence._id, data);
      setShowModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReopen = async () => {
    if (!onReopen) return;
    setIsSubmitting(true);
    try {
      await onReopen(occurrence._id);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Status badge styles
  const statusConfig = {
    open: {
      icon: Clock,
      label: 'Open',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-700',
      iconColor: 'text-amber-500',
    },
    resolved: {
      icon: CheckCircle2,
      label: 'Repaired',
      bgColor: 'bg-green-100',
      textColor: 'text-green-700',
      iconColor: 'text-green-500',
    },
    closed: {
      icon: XCircle,
      label: 'Closed',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-700',
      iconColor: 'text-gray-500',
    },
  };

  const config = statusConfig[occurrence.status] || statusConfig.open;
  const StatusIcon = config.icon;

  // Compact layout
  if (compact) {
    return (
      <>
        <div className="flex items-center gap-2">
          {/* Status badge */}
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${config.bgColor} ${config.textColor}`}
          >
            <StatusIcon size={10} className={config.iconColor} />
            {config.label}
          </span>

          {/* Action button */}
          {occurrence.status === 'open' && !readOnly && (
            <button
              onClick={() => setShowModal(true)}
              disabled={isSubmitting}
              data-action="toggle-repair"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Mark this occurrence as repaired"
            >
              <Wrench size={10} />
              Repair
            </button>
          )}

          {occurrence.status === 'resolved' && onReopen && !readOnly && (
            <button
              onClick={handleReopen}
              disabled={isSubmitting}
              data-action="toggle-repair"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Undo repair status"
            >
              <Undo2 size={10} />
              Undo
            </button>
          )}
        </div>

        {/* Repair details (compact) */}
        {occurrence.status === 'resolved' && occurrence.fixedAt && (
          <div className="text-[9px] text-gray-400 mt-0.5">
            Repaired {formatRepairDate(occurrence.fixedAt)}
            {occurrence.fixedNotes && (
              <span className="ml-1">· {occurrence.fixedNotes.slice(0, 30)}{occurrence.fixedNotes.length > 30 ? '...' : ''}</span>
            )}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <OccurrenceRepairModal
            occurrenceLabel={label || occurrence.location}
            violationCode={violationCode}
            violationDescription={violationDescription}
            occurrence={occurrence}
            onConfirm={handleConfirmRepair}
            onCancel={() => setShowModal(false)}
            isSubmitting={isSubmitting}
          />
        )}
      </>
    );
  }

  // Expanded layout (for detail views)
  return (
    <>
      <div className="border rounded-lg p-3 space-y-2">
        {/* Header with status */}
        <div className="flex items-center justify-between">
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
          >
            <StatusIcon size={14} className={config.iconColor} />
            {config.label}
          </span>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {occurrence.status === 'open' && !readOnly && (
              <button
                onClick={() => setShowModal(true)}
                disabled={isSubmitting}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Wrench size={14} />
                Mark Repaired
              </button>
            )}

            {occurrence.status === 'resolved' && onReopen && !readOnly && (
              <button
                onClick={handleReopen}
                disabled={isSubmitting}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Undo2 size={14} />
                Undo Repair
              </button>
            )}
          </div>
        </div>

        {/* Repair details (expanded) */}
        {occurrence.status === 'resolved' && (
          <div className="bg-green-50 rounded-md p-2 space-y-1">
            <div className="flex items-center gap-2 text-xs text-green-700">
              <CheckCircle2 size={12} />
              <span className="font-medium">Repaired {formatRepairDate(occurrence.fixedAt)}</span>
              {occurrence.fixedBy && (
                <span className="text-green-600">by {occurrence.fixedBy}</span>
              )}
            </div>
            {occurrence.fixedNotes && (
              <p className="text-xs text-green-600 pl-5">{occurrence.fixedNotes}</p>
            )}
          </div>
        )}

        {/* Original occurrence info */}
        <div className="text-xs text-gray-500 space-y-0.5">
          {occurrence.location && (
            <div><span className="font-medium">Location:</span> {occurrence.location}</div>
          )}
          {occurrence.notes && (
            <div><span className="font-medium">Notes:</span> {occurrence.notes}</div>
          )}
          <div className="text-[10px] text-gray-400">
            Discovered {formatRepairDate(occurrence.discoveredAt)}
            {occurrence.discoveredBy && ` by ${occurrence.discoveredBy}`}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <OccurrenceRepairModal
          occurrenceLabel={label || occurrence.location}
          violationCode={violationCode}
          violationDescription={violationDescription}
          occurrence={occurrence}
          onConfirm={handleConfirmRepair}
          onCancel={() => setShowModal(false)}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  );
}

export default OccurrenceRepairBlock;
